import { useEffect } from 'react';
import { locationService } from '@/services/locationService';
import { profileService } from '@/services/profileService';
import { commonService } from '@/services/commonService';
import { User } from '@/types/auth';

type UseCountryDetectionParams = {
  isAuthenticated: boolean;
  user: User | null;
  refreshUser: () => Promise<void>;
};

export function useCountryDetection({
  isAuthenticated,
  user,
  refreshUser,
}: UseCountryDetectionParams) {
  useEffect(() => {
    const KEY = 'detectedCountry';

    if (!isAuthenticated || !user) return;

    if (user.countryCode?.abbreviation) {
      const storedCountry = {
        country: user.countryCode.name,
        countryCode: user.countryCode.abbreviation,
      };
      localStorage.setItem(KEY, JSON.stringify(storedCountry));
      return;
    }

    const cachedCountry = localStorage.getItem(KEY);
    if (cachedCountry) {
      try {
        const parsed = JSON.parse(cachedCountry);
        if (parsed.country) {
          commonService
            .getCountryCodes()
            .then(countryList => {
              const match = (countryList.result || []).find(
                c =>
                  c.abbreviation === parsed.countryCode ||
                  c.name === parsed.country,
              );
              profileService
                .updateProfile({
                  countryCode: {
                    name: match?.name || parsed.country,
                    abbreviation:
                      match?.abbreviation || parsed.countryCode || undefined,
                    code: match?.code,
                  },
                })
                .catch(error => {
                  console.warn(
                    'Failed to save cached country to profile:',
                    error,
                  );
                });
            })
            .catch(error => {
              console.warn('Failed to fetch country codes:', error);
            });
        }
      } catch (error) {
        console.warn('Invalid country data in localStorage:', error);
        localStorage.removeItem(KEY);
      }
      return;
    }

    const detectAndSave = async () => {
      try {
        const resp = await locationService.detectCountry();

        if (resp?.result) {
          localStorage.setItem(KEY, JSON.stringify(resp.result));

          const countryList = await commonService.getCountryCodes();
          const match = (countryList.result || []).find(
            c =>
              c.abbreviation === resp.result.countryCode ||
              c.name === resp.result.country,
          );

          await profileService.updateProfile({
            countryCode: {
              name: match?.name || resp.result.country,
              abbreviation:
                match?.abbreviation || resp.result.countryCode || undefined,
              code: match?.code,
            },
          });

          await refreshUser();
        }
      } catch (error) {
        console.info(
          'Country detection skipped:',
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    };

    detectAndSave();
  }, [isAuthenticated, user, refreshUser]);
}
